import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChartLineUp, Eye, CursorClick, CalendarBlank, ArrowRight } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending_payment: 'Awaiting payment', pending_approval: 'In review',
  active: 'Live', rejected: 'Rejected', expired: 'Completed',
};
const STATUS_STYLE = {
  active: 'bg-emerald-100 text-emerald-700', pending_approval: 'bg-amber-100 text-amber-800',
  expired: 'bg-neutral-200 text-neutral-600', rejected: 'bg-red-100 text-red-700',
  pending_payment: 'bg-neutral-200 text-neutral-600',
};

export default function AdStatsPage() {
  const { token } = useParams();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/ads/stats/${token}`)
      .then((r) => setStats(r.data.stats))
      .catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] flex items-center justify-center px-4">
        <div className="bg-white border-2 border-black p-10 text-center shadow-[5px_5px_0px_#D95800]">
          <p className="font-black uppercase mb-2">Stats link not found</p>
          <p className="text-sm text-neutral-500 mb-4">Check the link from your confirmation, or contact support@constructhelp.com</p>
          <Link to="/advertising" className="text-[#D95800] font-bold underline">Learn about advertising →</Link>
        </div>
      </div>
    );
  }
  if (!stats) return <div className="min-h-screen bg-[#E8E9EB] flex items-center justify-center font-bold text-neutral-400">Loading…</div>;

  const cards = [
    { icon: Eye, label: 'Impressions', value: stats.impressions.toLocaleString(), testid: 'stat-impressions' },
    { icon: CursorClick, label: 'Clicks', value: stats.clicks.toLocaleString(), testid: 'stat-clicks' },
    { icon: ChartLineUp, label: 'Click-through rate', value: `${stats.ctr}%`, testid: 'stat-ctr' },
    { icon: CalendarBlank, label: 'Days remaining', value: stats.days_left ?? '—', testid: 'stat-days' },
  ];

  return (
    <div className="min-h-screen bg-[#E8E9EB]" data-testid="ad-stats-page">
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <p className="text-xs font-black uppercase text-[#D95800] mb-2">Advertiser Dashboard</p>
          <h1 className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight">{stats.company_name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className={`px-2.5 py-1 text-xs font-black rounded uppercase ${STATUS_STYLE[stats.status] || ''}`} data-testid="ad-stats-status">
              {STATUS_LABEL[stats.status] || stats.status}
            </span>
            <span className="text-neutral-400 text-sm font-bold">{stats.placement_label}{stats.target_trade_label ? ` — ${stats.target_trade_label}${stats.target_city_label ? ` (${stats.target_city_label})` : ''} only` : ''} · {stats.weeks} week{stats.weeks > 1 ? 's' : ''} · ${stats.price}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map(({ icon: Icon, label, value, testid }) => (
            <div key={label} className="bg-white border-2 border-black p-5 shadow-[3px_3px_0px_#000]" data-testid={testid}>
              <Icon size={22} weight="bold" className="text-[#D95800]" />
              <p className="text-3xl font-black mt-2">{value}</p>
              <p className="text-xs font-bold uppercase text-neutral-500">{label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border-2 border-black p-5 shadow-[3px_3px_0px_#000]">
          <p className="text-xs font-black uppercase text-neutral-400 mb-3">Your banner</p>
          <img src={stats.image_data} alt={`${stats.company_name} banner`} className="w-full max-h-32 object-cover border border-neutral-200" />
          {stats.starts_at && (
            <p className="text-xs text-neutral-500 mt-2">
              Running {new Date(stats.starts_at).toLocaleDateString()} → {stats.ends_at ? new Date(stats.ends_at).toLocaleDateString() : '—'}
            </p>
          )}
        </div>

        <div className="bg-[#D95800] border-2 border-neutral-900 p-8 text-center shadow-[5px_5px_0px_#000]">
          <h2 className="text-2xl font-black uppercase text-white mb-2">
            {stats.days_left === 0 || stats.status === 'expired' ? 'Your campaign has ended — keep the momentum' : 'Like the numbers? Lock in your next run'}
          </h2>
          <p className="text-orange-100 mb-5">Spots are one-per-page and first come, first served. Rebook in two minutes.</p>
          <Link to="/advertise" className="inline-flex h-12 px-8 items-center gap-2 bg-neutral-900 text-white font-black uppercase border-2 border-white" data-testid="ad-stats-renew">
            Renew / Book Again <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}
